import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FixedNav,
  Caption,
  Card,
  Button,
  Divider,
  StatStrip,
  AnimatedNumber,
  PulseDot,
  OnboardingModal,
  MODULE_SLIDES,
  GAME_RESULT_LABEL_FR,
  GAME_SOURCE_LABEL_FR,
} from '@/ui';
import { authApi } from '@/auth/api';
import { gameAutopsyApi, type GameSummary } from './api';

/**
 * P2 vague C'.4 — détecte si l'erreur d'import vient d'un token Lichess
 * expiré (Lichess API renvoie 401 → backend wrap en 502 `import_failed`
 * avec `detail: "Lichess games fetch failed: HTTP 401"`). Permet à l'UI
 * d'afficher un message ciblé + bouton de reconnexion au lieu du message
 * générique "Vérifie ton token Lichess" qui laissait Léon devoir trouver
 * lui-même comment se relogger.
 *
 * Cf. api/src/Services/LichessGameImporter.php :
 *   if ($status !== 200) {
 *     throw new RuntimeException('Lichess games fetch failed: HTTP ' . $status);
 *   }
 */
function isLichessTokenExpiredError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;
  const e = err as { status?: unknown; body?: unknown };
  if (e.status !== 502) return false;
  const body = e.body as { error?: unknown; detail?: unknown } | undefined;
  if (!body || typeof body !== 'object') return false;
  if (body.error !== 'import_failed') return false;
  // detail contient le message d'origine de l'exception : on cherche
  // "HTTP 401" (token expiré côté Lichess) ou "HTTP 403" (token révoqué).
  const detail = typeof body.detail === 'string' ? body.detail : '';
  return /HTTP\s*40[13]/i.test(detail);
}

/**
 * Game Autopsy — liste des parties (port V2).
 *
 * Source proto : `docs/prototypes/ui-kit-v2/.../GameAutopsy.jsx` §GameList.
 * Pattern visuel identique à Tactics/Endgame (PulseDot + display-2 + StatStrip
 * + filter tabs + grid), accent coral (L'Analyste, DESIGN §7).
 *
 * Wiring : `useQuery(['games', 'list', opening_slug])` avec filter optionnel
 * via `?opening_slug=` (bi-directional Opening Lab → Autopsy, backend #48 OK).
 * Filters côté client : Toutes / Défaites (DB-only en V1, pas de count mistakes
 * dans GameSummary → filter Blunders/Hors livre laissé en attendant T2 KPI
 * nocturnes qui ajoutera ces counts au /games endpoint).
 *
 * Le proto §GameList affiche aussi `outOfBookPly` + `mistakes`/`blunder` par
 * jeu : ces counts ne sont pas dans l'API actuelle, donc placeholder (—) tant
 * que MVP2 T1 (migration 010 `out_of_book_ply`) + T2 KPI agrégés ne sont pas
 * livrés. Cf. WAKEUP_MVP2_T2_FRONTEND_PORT.md §3.3.
 */

const RESULT_BADGE: Record<
  GameSummary['result'],
  { letter: string; color: string; bg: string; border: string }
> = {
  win: {
    letter: 'V',
    color: 'var(--volt-500)',
    bg: 'rgba(91,233,185,0.10)',
    border: 'rgba(91,233,185,0.30)',
  },
  loss: {
    letter: 'D',
    color: 'var(--coral-500)',
    bg: 'rgba(255,92,122,0.10)',
    border: 'rgba(255,92,122,0.30)',
  },
  draw: {
    letter: 'N',
    color: 'var(--ink-300)',
    bg: 'rgba(154,164,190,0.08)',
    border: 'var(--ink-700)',
  },
};

const CADENCE_LABEL: Record<GameSummary['cadence_bucket'], string> = {
  bullet: 'Bullet',
  blitz: 'Blitz',
  rapid: 'Rapid',
  classical: 'Classique',
  correspondence: 'Corres.',
};

type Filter = 'all' | 'loss' | 'win';

export function GameListPage(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const openingSlug = params.get('opening_slug') ?? undefined;

  const listQ = useQuery({
    queryKey: ['games', 'list', openingSlug],
    queryFn: () =>
      gameAutopsyApi.list({ limit: 50, ...(openingSlug ? { opening_slug: openingSlug } : {}) }),
  });

  const importMutation = useMutation({
    mutationFn: () => gameAutopsyApi.triggerImport(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['games', 'list'] }),
  });

  const games = useMemo(() => listQ.data?.games ?? [], [listQ.data]);
  const total = games.length;
  const counts = useMemo(
    () => ({
      win: games.filter((g) => g.result === 'win').length,
      loss: games.filter((g) => g.result === 'loss').length,
      draw: games.filter((g) => g.result === 'draw').length,
    }),
    [games],
  );

  const [filter, setFilter] = useState<Filter>('all');
  const visible = useMemo(() => {
    if (filter === 'loss') return games.filter((g) => g.result === 'loss');
    if (filter === 'win') return games.filter((g) => g.result === 'win');
    return games;
  }, [games, filter]);

  const filters: Array<{ id: Filter; label: string; count: number }> = [
    { id: 'all', label: 'Toutes', count: total },
    { id: 'loss', label: 'Défaites', count: counts.loss },
    { id: 'win', label: 'Victoires', count: counts.win },
  ];

  const chips = [
    { label: 'Parties', value: String(total), color: 'var(--paper-50)' },
    { label: 'V/N/D', value: `${counts.win}/${counts.draw}/${counts.loss}`, color: 'var(--coral-500)' },
  ];

  return (
    <>
      {/* T7 MVP2.5 — onboarding 1ère visite Game Autopsy (bug A8). */}
      <OnboardingModal
        moduleSlug="game-autopsy"
        moduleLabel="Game Autopsy"
        tone="coral"
        slides={MODULE_SLIDES['game-autopsy'] ?? []}
      />
      <FixedNav chips={chips} />
      <main className="mx-auto max-w-[1280px] px-6 py-8 flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <div className="flex items-center gap-2.5">
            <PulseDot color="var(--coral-500)" size={8} />
            <Caption color="coral">Game Autopsy</Caption>
          </div>
          <h1 className="font-display text-display-2 text-paper-50">
            Analyse des parties
          </h1>
          <p className="text-body-sm text-ink-300">
            {total} parties importées · Stockfish 16 NNUE depth 16, ~90 s par partie. Tourne
            dans ton navigateur.
            {openingSlug && (
              <>
                {' '}
                <button
                  type="button"
                  onClick={() => navigate('/game-autopsy', { replace: true })}
                  className="text-violet-500 hover:underline underline-offset-4"
                >
                  · filtré sur <span className="font-mono">{openingSlug}</span> (retirer le filtre)
                </button>
              </>
            )}
          </p>
        </header>

        {/* Stats strip */}
        <StatStrip className="flex-wrap items-stretch gap-x-8">
          <StatTile label="Parties" value={total} color="var(--paper-50)" />
          <Divider vertical />
          <div className="flex flex-col gap-1 min-w-[120px]">
            <Caption>V · N · D</Caption>
            <div className="font-mono text-base leading-none" style={{ fontWeight: 500 }}>
              <span className="text-volt-500">{counts.win}</span>
              <span className="text-ink-700 mx-1">·</span>
              <span className="text-ink-300">{counts.draw}</span>
              <span className="text-ink-700 mx-1">·</span>
              <span className="text-coral-500">{counts.loss}</span>
            </div>
          </div>
          <Divider vertical />
          <div className="flex flex-col gap-1 min-w-[110px]">
            <Caption>À analyser</Caption>
            <span
              className="font-mono leading-none"
              style={{ fontSize: 26, color: 'var(--coral-500)', fontWeight: 500 }}
              title="Détail gaffe/erreur/hors livre par partie : bientôt disponible"
            >
              —
            </span>
          </div>
          <Divider vertical />
          <div className="flex flex-col gap-1 min-w-[180px]">
            <Caption>Sources</Caption>
            <span className="font-mono text-[11px] leading-snug text-ink-300">
              <span className="text-paper-50">Lichess</span> OAuth · cron 1/h
              <br />
              <span className="text-paper-50">chess.com</span> public · cron 1/j
            </span>
          </div>
          <div className="ml-auto self-center">
            <Button
              variant="primary"
              accent="coral"
              onClick={() => importMutation.mutate()}
              disabled={importMutation.isPending}
            >
              {importMutation.isPending ? 'Import en cours…' : '⚡ Importer Lichess'}
            </Button>
          </div>
        </StatStrip>

        {importMutation.isSuccess && (
          <Card>
            <p className="text-body-sm text-volt-500">
              Import OK — <strong>{importMutation.data.imported_count}</strong> nouvelle(s),{' '}
              <strong>{importMutation.data.skipped_count}</strong> déjà connue(s).
            </p>
          </Card>
        )}
        {importMutation.isError && isLichessTokenExpiredError(importMutation.error) && (
          <Card>
            <p className="text-body-sm text-amber-500">
              Ton token Lichess a expiré. Reconnecte-toi pour relancer l'import.
            </p>
            <div className="mt-3">
              <Button
                variant="primary"
                accent="coral"
                onClick={async () => {
                  try {
                    const { auth_url } = await authApi.startLichessOAuth();
                    window.location.assign(auth_url);
                  } catch (e) {
                    console.error('[autopsy] re-login Lichess failed', e);
                  }
                }}
              >
                🔐 Reconnecte-toi à Lichess
              </Button>
            </div>
          </Card>
        )}
        {importMutation.isError && !isLichessTokenExpiredError(importMutation.error) && (
          <Card>
            <p className="text-body-sm text-coral-500">
              Import échoué. Réessaye dans quelques secondes. Si l'erreur persiste, préviens Valentin.
            </p>
          </Card>
        )}

        {/* Filter tabs */}
        <nav className="flex gap-2 flex-wrap">
          {filters.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-lg border font-display text-sm transition-all duration-150 ${
                  active
                    ? 'border-coral-500 bg-coral-500/10 text-paper-50'
                    : 'border-ink-700 bg-ink-800 text-ink-300 hover:border-ink-600 hover:text-paper-50'
                }`}
              >
                {f.label}
                <span className="ml-1.5 font-mono text-xs opacity-70">{f.count}</span>
              </button>
            );
          })}
        </nav>

        {listQ.isPending && (
          <Card>
            <p className="text-ink-300 italic">Chargement des parties…</p>
          </Card>
        )}
        {listQ.isError && (
          <Card>
            <p className="text-coral-500">Erreur de chargement.</p>
          </Card>
        )}
        {listQ.data && total === 0 && (
          <Card>
            <p className="text-body text-ink-300">
              Aucune partie pour l'instant. Lance « Importer mes parties Lichess » pour tirer
              tes dernières parties classées.
            </p>
          </Card>
        )}

        {/* Game list */}
        {visible.length > 0 && (
          <ul className="flex flex-col gap-2 list-none p-0">
            {visible.map((g) => (
              <li key={g.id}>
                <GameRow game={g} onClick={() => navigate(`/game-autopsy/${g.id}`)} />
              </li>
            ))}
          </ul>
        )}

        <footer className="pt-4">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="text-caption uppercase text-ink-300 hover:text-paper-50 transition-all duration-150"
          >
            ← Mission Control
          </button>
        </footer>
      </main>
    </>
  );
}

// ─── Sous-composants ──────────────────────────────────────────────────────

function StatTile({
  label,
  value,
  color,
  format,
}: {
  label: string;
  value: number;
  color: string;
  format?: (v: number) => string;
}): JSX.Element {
  return (
    <div className="flex flex-col gap-1 min-w-[80px]">
      <Caption>{label}</Caption>
      <AnimatedNumber
        value={value}
        {...(format ? { format } : {})}
        className="font-mono"
        style={{ fontSize: 26, color, lineHeight: 1, fontWeight: 500 }}
      />
    </div>
  );
}

function GameRow({ game, onClick }: { game: GameSummary; onClick: () => void }): JSX.Element {
  const r = RESULT_BADGE[game.result];
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center gap-4 px-4 py-3 bg-ink-800 border border-ink-700 rounded-xl transition-all duration-150 hover:border-coral-500/40 hover:shadow-card-hover animate-card-enter"
    >
      <span
        className="inline-flex items-center justify-center font-display font-bold flex-none"
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: r.bg,
          border: `1px solid ${r.border}`,
          color: r.color,
          fontSize: 22,
        }}
        aria-label={`Résultat : ${GAME_RESULT_LABEL_FR[game.result] ?? game.result}`}
      >
        {r.letter}
      </span>

      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className="font-display font-semibold text-paper-50 text-sm">
            {game.opening_name ?? 'Ouverture inconnue'}
          </span>
          {game.opening_eco && (
            <span className="font-mono text-[11px] text-ink-300">({game.opening_eco})</span>
          )}
        </div>
        <div className="mt-1 flex items-center gap-3 font-mono text-xs text-ink-300 flex-wrap">
          <span>
            {CADENCE_LABEL[game.cadence_bucket]} · {game.time_control}
          </span>
          <span>{game.color === 'white' ? '◻ Blancs' : '◼ Noirs'}</span>
          <span>
            <span className="text-paper-50">{game.rating_self}</span>
            <span className="mx-1">vs</span>
            <span
              style={{
                color: game.rating_opp > game.rating_self ? 'var(--amber-500)' : 'var(--ink-300)',
              }}
            >
              {game.rating_opp}
            </span>
          </span>
          <span className="text-ink-300">· source {GAME_SOURCE_LABEL_FR[game.source] ?? game.source}</span>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-none">
        <span className="font-mono text-[11px] text-ink-300 whitespace-nowrap">
          {formatDate(game.played_at)}
        </span>
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-coral-500 text-coral-500 text-[11px] uppercase font-display tracking-wider">
          Analyser →
        </span>
      </div>
    </button>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso.replace(' ', 'T') + 'Z');
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
}
